import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { FiLock, FiEye, FiEyeOff } from "react-icons/fi";
import { FaArrowRight } from "react-icons/fa";
import api from "../services/api";

const ResetPassword = () => {
  const navigate = useNavigate();
  const { token } = useParams();

  const [form, setForm]                           = useState({ password:"", confirmPassword:"" });
  const [message, setMessage]                     = useState("");
  const [loading, setLoading]                     = useState(false);
  const [showPassword, setShowPassword]           = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e) => setForm(p => ({ ...p, [e.target.name]:e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.password || !form.confirmPassword) { setMessage("Please fill all fields."); return; }
    if (form.password !== form.confirmPassword)  { setMessage("Passwords do not match."); return; }
    try {
      setLoading(true);
      const res = await api.post("/auth/reset-password", { token, newPassword:form.password });
      if (res.data.success) {
        setMessage("Password reset successful.");
        setTimeout(() => navigate("/login"), 2000);
      }
    } catch (err) {
      setMessage(err.response?.data?.message || "Something went wrong.");
    } finally { setLoading(false); }
  };

  const inputCls = "w-full h-full bg-transparent outline-none border-none px-4 text-[#111111]";
  const rowCls   = "flex items-center h-[68px] rounded-[22px] border border-[#EFE3D2] bg-[#FFFDFC] px-5 transition-all duration-300 focus-within:border-[#F29A0C]";

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#F8F5EF] flex items-center justify-center px-6">

      {/* Background */}
      <div className="absolute inset-0">
        <img src="/assets/image 25.jpeg" alt="" className="w-full h-full object-cover scale-110 opacity-[0.74]"/>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,154,12,0.16),transparent_35%)]"/>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(255,180,80,0.12),transparent_35%)]"/>
        <div className="absolute top-[-200px] left-[-160px] w-[500px] h-[500px] rounded-full bg-[#F29A0C]/20 blur-[140px]"/>
        <div className="absolute bottom-[-240px] right-[-180px] w-[500px] h-[500px] rounded-full bg-[#FFB347]/14 blur-[140px]"/>
      </div>

      {/* Card */}
      <motion.div initial={{ opacity:0, y:30 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.8 }}
        className="relative w-full max-w-[500px] rounded-[36px] border p-8 md:p-10 z-20 overflow-hidden border-white/40 bg-white/55 backdrop-blur-[40px] shadow-[0_60px_180px_rgba(242,154,12,0.16)]">

        <div className="relative z-20">
          {/* Logo */}
          <motion.div whileHover={{ rotate:6, scale:1.04 }}
            className="w-[74px] h-[74px] mx-auto rounded-[24px] overflow-hidden border border-white/50 bg-white shadow-[0_20px_60px_rgba(0,0,0,0.06)]">
            <img src="/assets/logo.jpeg" alt="" className="w-full h-full object-cover"/>
          </motion.div>

          {/* Header */}
          <div className="text-center mt-8">
            <p className="text-[#F29A0C] text-[11px] tracking-[4px] uppercase font-semibold">SECURITY UPDATE</p>
            <h1 className="text-[44px] md:text-[42px] leading-[0.92] tracking-[4px] font-semibold text-[#111111] mt-5" style={{ fontFamily:"Poppins" }}>
              Reset Password
            </h1>
            <p className="text-[#666666] text-[15px] leading-[30px] mt-5">
              Create a new secure password to continue your wellness journey.
            </p>
          </div>

          <div className="absolute top-[-120px] right-[-100px] w-[260px] h-[260px] rounded-full bg-[#F29A0C]/20 blur-[100px]"/>

          {/* Message */}
          {message && (
            <div className="mt-6 rounded-[22px] border border-white/50 bg-white/70 backdrop-blur-xl px-5 py-4 text-[#A15C07] text-sm">
              {message}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="mt-10 space-y-5">
            <div className={rowCls}>
              <FiLock className="text-[#F29A0C]"/>
              <input type={showPassword ? "text" : "password"} name="password" placeholder="New Password" value={form.password} onChange={handleChange} className={inputCls}/>
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-[#999999]">
                {showPassword ? <FiEyeOff/> : <FiEye/>}
              </button>
            </div>

            <div className={rowCls}>
              <FiLock className="text-[#F29A0C]"/>
              <input type={showConfirmPassword ? "text" : "password"} name="confirmPassword" placeholder="Confirm Password" value={form.confirmPassword} onChange={handleChange} className={inputCls}/>
              <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="text-[#999999]">
                {showConfirmPassword ? <FiEyeOff/> : <FiEye/>}
              </button>
            </div>

            <motion.button whileHover={{ y:-2 }} whileTap={{ scale:0.98 }} type="submit"
              className="w-full h-[70px] rounded-[22px] mt-2 bg-gradient-to-r from-[#F29A0C] to-[#FF8400] text-white font-semibold flex items-center justify-center gap-3 shadow-[0_20px_60px_rgba(242,154,12,0.18)]">
              {loading ? "Resetting..." : "Reset Password"}<FaArrowRight/>
            </motion.button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-[#666666] text-[15px] mt-8">
          Back to <Link to="/login" className="text-[#F29A0C] font-semibold ml-2">Login</Link>
        </p>
      </motion.div>
    </div>
  );
};

export default ResetPassword;